import React, { useEffect, useState, useRef } from "react";
import Breadcrumb from "../../../component/breadcrumb/Breadcrumb";
import Spinner from "../../../component/loadingbar/Spinner";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import Swal from "sweetalert2";
import { triggerNotification } from "../../../component/message/Msg";
import TableViewer from "../../../component/tableViewer/TableViewer";
import DataTable from "react-data-table-component";

const PromoCodeDashboard = () => {
  const token = config.token;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Stats Modal
  const [showUsagesModal, setShowUsagesModal] = useState(false);
  const [usages, setUsages] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [selectedCode, setSelectedCode] = useState("");

  const [rowData, setRowData] = useState([]);

  const formatDDMMYYYY = (dateString) => {
    if (!dateString) return "Never";
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  };

  const fetchPromoCodes = () => {
    setLoading(true);
    fetch(`${config.host_url}promo-codes`, {
      method: "GET",
      headers: { token },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 200) {
          setPromoCodes(data.data);
          setStats(data.stats);
          setIsAdmin(data.isAdmin);

          const rData = data.data.map((code) => ({
            idNumber: code.id,
            code: code.code,
            creator_name: code.creator_name || "Self",
            expiry_date: code.expiry_date ? formatDDMMYYYY(code.expiry_date) : "Never",
            max_uses: code.max_uses ? code.max_uses : "Unlimited",
            discount: `${code.discount_value} ${code.discount_type === 'percent' ? '%' : 'Fixed'}`,
            total_used: code.total_used,
            statusText: code.is_active ? "Active" : "Inactive"
          }));
          setRowData(rData);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        triggerNotification("error", "Error fetching promo codes");
      });
  };

  useEffect(() => {
    fetchPromoCodes();
  }, [token]);

  const handleDeleteBtn = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this promo code?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${config.host_url}promo-codes/${id}`, {
          method: "DELETE",
          headers: { token },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status === 200) {
              triggerNotification("success", "Promo code deleted");
              fetchPromoCodes();
            } else {
              triggerNotification("error", "Failed to delete");
            }
          });
      }
    });
  };

  const handleStatusChange = (id) => {
    const code = promoCodes.find(c => c.id == id);
    if(!code) return;
    const newStatus = code.is_active ? 0 : 1;
    fetch(`${config.host_url}promo-codes/${id}/toggle-status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", token },
      body: JSON.stringify({ is_active: newStatus }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 200) {
          triggerNotification("success", "Status updated");
          fetchPromoCodes();
        } else {
          triggerNotification("error", "Failed to update status");
        }
      });
  };

  const handleView = (id) => {
    const codeObj = promoCodes.find(c => c.id == id);
    setSelectedCode(codeObj.code);
    setUsageLoading(true);
    setShowUsagesModal(true);

    fetch(`${config.host_url}promo-codes/usages/${id}`, {
      method: "GET",
      headers: { token },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 200) {
          setUsages(data.data);
        }
        setUsageLoading(false);
      })
      .catch(() => {
        setUsageLoading(false);
      });
  };

  const handleAddCode = () => {
    Swal.fire({
      title: 'Add New Promo Code',
      html: `
        <div class="mb-3 mt-3">
          <input type="text" id="code" class="form-control" placeholder="Promo Code (Leave blank to auto-generate)">
        </div>
        <div class="mb-3">
          <select id="discount_type" class="form-select">
            <option value="percent">Percentage Discount (%)</option>
            <option value="fixed">Fixed Amount Discount</option>
          </select>
        </div>
        <div class="mb-3">
          <input type="number" id="discount_value" class="form-control" placeholder="Discount Value">
        </div>
        <div class="mb-3">
          <input type="number" id="max_uses" class="form-control" placeholder="Max Uses (Leave blank for unlimited)">
        </div>
        <div class="mb-3 text-start">
          <label class="form-label ms-1">Expiry Date (Optional)</label>
          <input type="date" id="expiry_date" class="form-control">
        </div>
      `,
      confirmButtonText: 'Create',
      focusConfirm: false,
      preConfirm: () => {
        const discount_value = Swal.getPopup().querySelector('#discount_value').value;
        if (!discount_value) {
          Swal.showValidationMessage(`Please enter a discount value`);
        }
        return {
          code: Swal.getPopup().querySelector('#code').value,
          discount_type: Swal.getPopup().querySelector('#discount_type').value,
          discount_value: discount_value,
          max_uses: Swal.getPopup().querySelector('#max_uses').value,
          expiry_date: Swal.getPopup().querySelector('#expiry_date').value
        }
      }
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${config.host_url}promo-codes`, {
          method: "POST",
          headers: { "Content-Type": "application/json", token },
          body: JSON.stringify(result.value),
        })
          .then(res => res.json())
          .then(data => {
            if (data.status === 200) {
              triggerNotification("success", `Promo code created: ${data.code}`);
              fetchPromoCodes();
            } else if (data.status === 400) {
              triggerNotification("error", data.msg);
            } else {
              triggerNotification("error", "Failed to create promo code");
            }
          });
      }
    })
  };

  const colDefs = [
    { headerName: "#", width: 50, valueFormatter: (params) => `${parseInt(params.node.id) + 1}` },
    { field: "code", filter: true },
    ...(isAdmin ? [{ field: "creator_name", headerName: "Created By", filter: true }] : []),
    { field: "discount", filter: true },
    { field: "max_uses", filter: true },
    { field: "expiry_date", filter: true },
    { field: "total_used", filter: true },
    { 
      field: "statusText", 
      headerName: "Status", 
      filter: true,
      cellRenderer: (params) => {
        const isActive = params.data.statusText === "Active";
        return (
          <span 
            className={`badge cursor-pointer ${isActive ? 'bg-success' : 'bg-secondary'}`}
            onClick={() => handleStatusChange(params.data.idNumber)}
          >
            {params.data.statusText}
          </span>
        );
      }
    },
  ];

  const usageColumns = [
    { name: 'User Name', selector: row => row.user_name, sortable: true },
    { name: 'Email', selector: row => row.user_email || 'N/A', sortable: true },
    { name: 'Discount Received', selector: row => row.discount_amount, sortable: true },
    { name: 'Date Used', selector: row => row.used_at ? formatDDMMYYYY(row.used_at) : 'N/A', sortable: true },
  ];

  return (
    <>
      <Breadcrumb txt="Manage Promo Codes" subpath={["View Promo Codes"]} />
      <div className="col-xxl">
        {stats && (
          <div className="flex justify-between px-4 py-2 row mb-4">
            <div className="col-sm-3">
              <div className="card" style={{ height: "125px" }}>
                <div className="card-body">
                  <h5>{stats.total_coupons}</h5>
                  <p className="mb-lg-2 mb-xl-3 text-muted">Total Coupons</p>
                </div>
              </div>
            </div>
            <div className="col-sm-3">
              <div className="card" style={{ height: "125px" }}>
                <div className="card-body">
                  <h5>{stats.total_used}</h5>
                  <p className="mb-lg-2 mb-xl-3 text-muted">Total Used</p>
                </div>
              </div>
            </div>
            <div className="col-sm-3">
              <div className="card" style={{ height: "125px" }}>
                <div className="card-body">
                  <h5>{stats.active_coupons}</h5>
                  <p className="mb-lg-2 mb-xl-3 text-muted">Active Coupons</p>
                </div>
              </div>
            </div>
            <div className="col-sm-3">
              <div className="card" style={{ height: "125px" }}>
                <div className="card-body">
                  <h5>₹{stats.total_discount_given || 0}</h5>
                  <p className="mb-lg-2 mb-xl-3 text-muted">Total Discount Given</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card mb-4">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">View Promo Codes</h5>
            <button type="button" className="btn btn-primary" onClick={handleAddCode}>
              Add Promo Code
            </button>
          </div>
          {loading ? (
            <Spinner />
          ) : (
            <div className="px-3 pb-3 table-responsive">
              <TableViewer
                rowData={rowData}
                colDefs={colDefs}
                onDelete={handleDeleteBtn}
                onView={handleView}
                suppressHorizontalScroll={false}
              />
            </div>
          )}
        </div>
      </div>

      {showUsagesModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Usages for {selectedCode}</h5>
                <button type="button" className="btn-close" onClick={() => setShowUsagesModal(false)}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {usageLoading ? <Spinner /> : (
                  <DataTable
                    columns={usageColumns}
                    data={usages}
                    pagination
                    fixedHeader
                    fixedHeaderScrollHeight="400px"
                  />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowUsagesModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PromoCodeDashboard;
